import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Topnavbar from '../Header-pages/Topnavbar'
import Navbar from '../Header-pages/Navbar'
import Footer from '../Header-pages/Footer'
import { API_URL } from '../utils/Function'
import '../Css/ProjectView.css'

export default function Projectview() {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeFilter, setActiveFilter] = useState('All')
  const [dynamicCategories, setDynamicCategories] = useState([])
  const [isAnimating, setIsAnimating] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [showLeftGradient, setShowLeftGradient] = useState(false)
  const [showRightGradient, setShowRightGradient] = useState(true)
  const navRef = useRef(null)
  const dropdownRef = useRef(null)
  const navigate = useNavigate()

  // Inline CSS styles - Simplified without media queries
  const styles = {
    page: {
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px',
    },
    hero: {
      position: 'relative',
      height: '300px',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    },
    heroOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 100%)',
    },
    breadcrumbs: {
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    breadcrumbLink: {
      color: 'rgba(255, 255, 255, 0.9)',
      textDecoration: 'none',
      transition: 'color 0.3s',
      cursor: 'pointer',
    },
    breadcrumbSeparator: {
      margin: '0 10px',
      color: 'rgba(255, 255, 255, 0.7)',
      
    },
    heroTitle: {
      fontSize: '3rem',
      fontWeight: '700',
      color: 'white',
      marginBottom: '10px',
    },
    heroSubtitle: {
      fontSize: '1.3rem',
      color: 'rgba(255, 255, 255, 0.9)',
    },
    content: {
      padding: '40px 0 60px',
    },
    sectionTitle: {
      textAlign: 'center',
      fontSize: '2.5rem',
      fontWeight: '700',
      color: '#333',
      marginBottom: '40px',
    },
    projectCard: {
      borderRadius: '20px',
      padding: '20px',
      minHeight: '280px',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden',
    },
    projectCardHover: {
      transform: 'translateY(-5px)',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
    },
    projectImageHover: {
      transform: 'scale(1.05)',
    },
    loading: {
      textAlign: 'center',
      padding: '100px 20px',
      fontSize: '1.2rem',
    },
    error: {
      textAlign: 'center',
      padding: '100px 20px',
      fontSize: '1.2rem',
      color: '#dc3545',
    },
    noProjects: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#666',
      fontSize: '1.1rem',
      gridColumn: '1 / -1',
    },
  };

  // Fetch all projects
 useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        // Added params for ordering
        const response = await fetch(`${API_URL}/project.php/get?orderBy=display_order&orderDir=ASC`);
        const data = await response.json();
        
        if (data.success) {
          setProjects(data.projects || []);
          
          const categories = [...new Set(data.projects
            .map(project => project.category_name)
            .filter(Boolean)
          )];
          
          setDynamicCategories(categories);
        } else {
          setError(data.error || 'Failed to fetch projects');
        }
      } catch (err) {
        setError('Network error: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Check scroll position for gradient visibility
  useEffect(() => {
    const checkScroll = () => {
      if (navRef.current && allCategories.length > 5) {
        const { scrollLeft, scrollWidth, clientWidth } = navRef.current;
        setShowLeftGradient(scrollLeft > 0);
        setShowRightGradient(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };

    const navElement = navRef.current;
    if (navElement && allCategories.length > 5) {
      navElement.addEventListener('scroll', checkScroll);
      checkScroll(); // Initial check
    }

    return () => {
      if (navElement) {
        navElement.removeEventListener('scroll', checkScroll);
      }
    };
  }, [dynamicCategories]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Animation function for category change
  const handleCategoryChange = (category) => {
    if (category === activeFilter) return;
    
    setIsAnimating(true);
    setIsDropdownOpen(false);
    
    setTimeout(() => {
      setActiveFilter(category);
      
      setTimeout(() => {
        setIsAnimating(false);
      }, 50);
    }, 300);
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Scroll navigation horizontally
  const scrollNav = (direction) => {
    if (navRef.current && allCategories.length > 5) {
      const scrollAmount = 200;
      navRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Handle project click to navigate to details page
  const handleProjectClick = (project) => {
    if (project.slug) {
      navigate(`/project/${project.slug}`);
    } else {
      navigate(`/project/${project.id}`);
    }
  };

  // Function to get project image
const getProjectImage = (project) => {
  // Helper function to convert relative URLs to absolute
  const getAbsoluteUrl = (url) => {
    if (!url) return null;
    
    // If it's already a full URL, return as is
    if (url.startsWith('http') || url.startsWith('//')) {
      return url;
    }
    
    // If it starts with /, it's relative to domain root
    if (url.startsWith('/')) {
      return `${window.location.origin}${url}`;
    }
    
    // Otherwise, assume it's relative to API URL
    return `${API_URL}/${url.replace(/^\//, '')}`;
  };

  // Try featured image first
  if (project.featured_image_url) {
    const absoluteUrl = getAbsoluteUrl(project.featured_image_url);
    return absoluteUrl;
  }
  
  // Then try gallery images
  if (project.gallery_images_urls && project.gallery_images_urls.length > 0) {
    const galleryUrls = Array.isArray(project.gallery_images_urls) 
      ? project.gallery_images_urls 
      : JSON.parse(project.gallery_images_urls || '[]');
    
    if (galleryUrls.length > 0) {
      const absoluteUrl = getAbsoluteUrl(galleryUrls[0]);
      return absoluteUrl || '/images/placeholder-project.png';
    }
  }
  
  return '/images/placeholder-project.png';
};
  // Function to get dynamic color (matching Ourlatestworks)
  const getProjectColor = (project) => {
    if (project.card_color) {
      return project.card_color;
    }
    
    const colors = [
      '#0EA5E9', '#008080', '#231834', '#FFB006', 
      '#0489A2', '#0C090C', '#535148', '#5B7674',
      '#0D6EFD', '#EC078A', '#E8A77E', '#9374FF'
    ];
    
    const index = project.title ? project.title.length % colors.length : 0;
    return colors[index];
  };

  // Group projects by category for filtering
  const groupedProjects = {
    'All': projects,
    ...dynamicCategories.reduce((acc, category) => {
      acc[category] = projects.filter(project => 
        project.category_name === category
      );
      return acc;
    }, {})
  };

  const filteredProjects = groupedProjects[activeFilter] || [];
  const allCategories = ['All', ...dynamicCategories];
  const shouldShowScrollButtons = allCategories.length > 5;

  if (loading) {
    return (
      <>
        <Topnavbar />
        <Navbar />
        <div style={styles.loading}>Loading projects...</div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Topnavbar />
        <Navbar />
        <div style={styles.error}>
          <p>{error}</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Topnavbar />
      <Navbar />
      
      <div style={styles.page}>
        {/* Hero Section */}
        <div style={{...styles.hero, backgroundImage: `url("/images/Productview.jpg")`}}>
          <div style={styles.heroOverlay}>
            <div style={styles.container}>
              {/* Breadcrumbs */}
              <div style={styles.breadcrumbs}>
                <a onClick={()=>navigate('/')} style={styles.breadcrumbLink}>Home</a>
                <span style={styles.breadcrumbSeparator}>/</span>
                <span className='text-white' style={{cursor:'pointer'}}>All Projects</span>
              </div>

              {/* Page Title */}
              <h1 style={styles.heroTitle}>Our Projects</h1>
              <p style={styles.heroSubtitle}>Explore our portfolio of innovative solutions and creative works</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={styles.content}>
          <div style={styles.container}>
            <h2 style={styles.sectionTitle}>Our Portfolio</h2>
            
            {/* Desktop Navigation with Scroll */}
            <div className="project-view-nav-wrapper">
              {shouldShowScrollButtons && showLeftGradient && (
                <div className="project-nav-gradient project-nav-gradient-left"></div>
              )}
              
              <nav 
                ref={navRef}
                className={`project-view-nav project-desktop-nav project-scrollable-nav ${allCategories.length > 5 ? 'project-more-than-five' : ''}`}
              >
                {allCategories.map(category => (
                  <button 
                    key={category}
                    className={`project-nav-link ${activeFilter === category ? 'project-active' : ''}`}
                    onClick={() => handleCategoryChange(category)}
                  >
                    {category}
                  </button>
                ))}
              </nav>

              {shouldShowScrollButtons && showRightGradient && (
                <div className="project-nav-gradient project-nav-gradient-right"></div>
              )}

              {/* Scroll buttons only when more than 5 categories */}
              {shouldShowScrollButtons && (
                <>
                  <button 
                    className="project-nav-scroll-button project-nav-scroll-left"
                    onClick={() => scrollNav('left')}
                    aria-label="Scroll left"
                  >
                    ‹
                  </button>
                  <button 
                    className="project-nav-scroll-button project-nav-scroll-right"
                    onClick={() => scrollNav('right')}
                    aria-label="Scroll right"
                  >
                    ›
                  </button>
                </>
              )}
            </div>

            {/* Mobile Dropdown Navigation */}
            <div className="project-mobile-nav-container" ref={dropdownRef}>
              <button className="project-mobile-nav-toggle" onClick={toggleDropdown}>
                <span>{activeFilter}</span>
                <svg 
                  className={`project-dropdown-arrow ${isDropdownOpen ? 'project-open' : ''}`} 
                  width="12" 
                  height="8" 
                  viewBox="0 0 12 8" 
                  fill="none"
                >
                  <path 
                    d="M1 1.5L6 6.5L11 1.5" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              
              {isDropdownOpen && (
                <div className="project-mobile-dropdown">
                  {allCategories.map(category => (
                    <button
                      key={category}
                      className={`project-mobile-nav-link ${activeFilter === category ? 'project-active' : ''}`}
                      onClick={() => handleCategoryChange(category)}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Projects Grid - Using CSS classes for responsiveness */}
            <div className={`projects-grid-container ${isAnimating ? 'fade-out' : 'fade-in'}`}>
              {filteredProjects.length === 0 ? (
                <div style={styles.noProjects}>
                  <p>No projects found in this category.</p>
                </div>
              ) : (
                filteredProjects.map((project) => (
                  <div
                    key={project.id}
                    className="project-card-item"
                    style={{
                      backgroundColor: getProjectColor(project)
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = styles.projectCardHover.transform;
                      e.currentTarget.style.boxShadow = styles.projectCardHover.boxShadow;
                      
                      // Add image hover effect
                      const image = e.currentTarget.querySelector('.project-image');
                      if (image) {
                        image.style.transform = styles.projectImageHover.transform;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = styles.projectCard.boxShadow;
                      
                      // Remove image hover effect
                      const image = e.currentTarget.querySelector('.project-image');
                      if (image) {
                        image.style.transform = 'none';
                      }
                    }}
                    onClick={() => handleProjectClick(project)}
                  >
                    {/* Project Header */}
                    <div className="project-card-header">
                      <div className="project-title-section">
                        <span className="project-card-title">{project.title}</span>
                        <span className="project-card-category">
                          {project.category_name || 'Project'}
                        </span>
                      </div>
                      <span className="project-learn-more">Learn More</span>
                    </div>

                    {/* Project Image */}
                    <div className="project-image-container">
                      <img
                        src={getProjectImage(project)}
                        alt={project.title}
                        className={`project-image ${isAnimating ? 'image-fade-out' : 'image-fade-in'}`}
                        onError={(e) => {
                          e.target.src = '/images/placeholder-project.png';
                        }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}